import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Percent, ArrowUpRight } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";

const C = {
  navy: "#0B1426",
  blue: "#022CED",
  gold: "#F0B83A",
  grey: "#475569",
  ink: "#111827",
  lavender2: "#E2DEFA",
  cream: "#F8F7FB",
};

const FALLBACK = {
  spot: {
    pairs: [
      { pair: "BTC/USDT", maker_fee_pct: 0.1, taker_fee_pct: 0.1 },
      { pair: "ETH/USDT", maker_fee_pct: 0.1, taker_fee_pct: 0.1 },
    ],
  },
  otc: { service_fee_pct: 0.1, spread_buy_pct: 0.5, spread_sell_pct: 0.5 },
  p2p: { maker_pct: 0, taker_pct: 0.2 },
  withdrawals: [
    { asset: "BTC", network: "BTC", fee: "0.0005", unit: "BTC" },
    { asset: "USDT", network: "TRC20", fee: "1", unit: "USDT" },
  ],
  tax: {
    gst_rate_pct: 18,
    tds: {
      spot: { tds_percentage: 1, minimum_amount_inr: 10000, deduct_from: "seller", enabled: true },
      p2p: { tds_percentage: 1, minimum_amount_inr: 10000, deduct_from: "seller", enabled: true },
      otc: { tds_percentage: 1, minimum_amount_inr: 50000, deduct_from: "seller", enabled: true },
    },
  },
};

function pct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n}%`;
}

function fmtInr(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function FeeTable({ headers, rows, testid }) {
  return (
    <div
      className="overflow-x-auto rounded-[14px] border"
      style={{ borderColor: C.lavender2 }}
      data-testid={testid}
    >
      <table className="w-full text-[14px]">
        <thead>
          <tr className="text-left" style={{ background: C.cream }}>
            {headers.map((h) => (
              <th
                key={h}
                className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.12em]"
                style={{ color: C.grey }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t" style={{ borderColor: C.lavender2 }}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-5 py-3.5 ${j > 0 ? "text-right tabular-nums" : ""}`}
                  style={{ color: j === 0 ? C.navy : C.ink }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeeSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-14" data-testid={`fees-section-${id}`}>
      <h2
        className="text-[22px] lg:text-[26px] font-bold mb-4"
        style={{ color: C.navy, letterSpacing: "-0.01em" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

const TDS_LABELS = { spot: "Spot", otc: "OTC" };

export default function FeesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/api/fees/schedule")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null));
  }, []);

  const f = data || FALLBACK;
  const spotPairs = f.spot?.pairs?.length ? f.spot.pairs : FALLBACK.spot.pairs;
  const withdrawals = f.withdrawals?.length ? f.withdrawals : FALLBACK.withdrawals;
  const tdsModules = f.tax?.tds || FALLBACK.tax.tds;

  return (
    <div className="min-h-screen bg-white" data-testid="fees-page">
      <Header />

      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, #fff 0%, ${C.cream} 100%)` }}
      >
        <div className="cm-container relative pt-24 pb-16">
          <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-cm-blue">
            <Percent size={14} />
            Fee schedule
          </div>
          <h1
            className="mt-4 max-w-[760px]"
            style={{
              fontWeight: 800,
              fontSize: "clamp(32px, 4.5vw, 56px)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: C.navy,
            }}
          >
            Transparent fees across spot &amp; OTC
          </h1>
          <p className="mt-5 max-w-[680px] text-[15px] leading-[1.8]" style={{ color: C.grey }}>
            Rates below reflect what CoinMBX applies at execution. Per-market spot fees and
            network withdrawal fees may vary. Material changes are announced at least seven days
            in advance.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="cm-container max-w-[920px]">
          <FeeSection id="spot" title="Spot trading">
            <p className="mb-4 text-[14px] leading-[1.75]" style={{ color: C.grey }}>
              Maker/taker fees are set per market and charged on each fill.
            </p>
            <FeeTable
              testid="fees-spot-table"
              headers={["Market", "Maker", "Taker"]}
              rows={spotPairs.map((p) => [p.pair, pct(p.maker_fee_pct), pct(p.taker_fee_pct)])}
            />
          </FeeSection>

          <FeeSection id="otc" title="OTC desk">
            <p className="mb-4 text-[14px] leading-[1.75]" style={{ color: C.grey }}>
              OTC quotes apply a spread on the mid price plus a service fee on the trade value.
            </p>
            <FeeTable
              testid="fees-otc-table"
              headers={["Component", "Rate"]}
              rows={[
                ["Service fee", pct(f.otc.service_fee_pct)],
                ["Buy spread (above mid)", pct(f.otc.spread_buy_pct)],
                ["Sell spread (below mid)", pct(f.otc.spread_sell_pct)],
              ]}
            />
          </FeeSection>

          <FeeSection id="withdrawals" title="Crypto withdrawals">
            <p className="mb-4 text-[14px] leading-[1.75]" style={{ color: C.grey }}>
              Network fees are flat amounts per asset and chain, deducted from your withdrawal.
            </p>
            <FeeTable
              testid="fees-withdraw-table"
              headers={["Asset", "Network", "Fee"]}
              rows={withdrawals.map((w) => [
                w.asset,
                w.network,
                `${w.fee} ${w.unit}`,
              ])}
            />
          </FeeSection>

          <FeeSection id="taxes" title="Taxes &amp; statutory charges">
            <p className="mb-4 text-[14px] leading-[1.75]" style={{ color: C.grey }}>
              TDS under Section 194S applies when trade value meets the minimum threshold.
              GST may apply to platform service fees where required by law.
            </p>
            <FeeTable
              testid="fees-tax-table"
              headers={["Module", "TDS rate", "Min. trade value", "Deducted from"]}
              rows={Object.entries(tdsModules)
                .filter(([mod]) => mod !== "p2p")
                .map(([mod, t]) => [
                TDS_LABELS[mod] || mod.toUpperCase(),
                t.enabled === false ? "Disabled" : pct(t.tds_percentage),
                fmtInr(t.minimum_amount_inr),
                t.deduct_from === "seller" ? "Seller" : t.deduct_from,
              ])}
            />
            <p className="mt-4 text-[13px] leading-[1.75]" style={{ color: C.grey }}>
              GST on platform fees (where applicable): {pct(f.tax.gst_rate_pct)}. See our{" "}
              <Link to="/terms#fees" className="text-cm-blue font-semibold hover:underline">
                Terms &amp; Conditions
              </Link>{" "}
              for full tax responsibilities.
            </p>
          </FeeSection>

          <div
            className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 rounded-[14px] border"
            style={{ background: C.cream, borderColor: C.lavender2 }}
            data-testid="fees-cta"
          >
            <div>
              <div className="text-[15px] font-bold text-cm-navy">Ready to trade?</div>
              <div className="mt-1 text-[13px] text-cm-grey">
                Start on spot markets with the fee structure shown above.
              </div>
            </div>
            <Link
              to="/trade/spot/BTC-USDT"
              className="inline-flex items-center justify-center gap-2 h-[44px] px-5 rounded-[12px] bg-cm-blue text-white font-bold text-[14px] hover:bg-cm-blue-deep transition"
              data-testid="fees-start-trading"
            >
              Start trading <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
